import type { JSONContent } from '@tiptap/core';
import { ContentBlockType, MarkdownBlock, PopoverData } from '../../model';
import { BlockToTiptapConverter, ConverterContext } from '../blocks-to-tiptap.converter';
import {
  convertCustomIconNodesToText,
  convertEmojiNodesToText,
  convertMathNodesToText,
  convertPopoversToMarkers,
  convertTextColorsToMarkers,
  processNodeForCustomIcons,
  processNodeForMath,
  processNodeForPopovers,
  processNodeForTextColors,
} from './processors';
import { getMarkdownManager } from './utils/markdown-manager.util';
import { escapeBackslashesInMath, escapeTildesInMath } from './utils/markdown.util';

type ConvertTiptapToMarkdownBlockFn = (
  nodes: JSONContent[],
  id: string,
  customCss?: string,
  context?: ConverterContext,
  customStyle?: Record<string, string>,
) => MarkdownBlock;

/**
 * Converts a MarkdownBlock to Tiptap JSON nodes.
 * Uses @tiptap/markdown MarkdownManager to parse the markdown string into Tiptap nodes.
 * Post-processes to convert LaTeX math syntax to inlineMath/blockMath nodes.
 * Post-processes to convert popover markers to popover marks.
 *
 * @param block - The MarkdownBlock to convert
 * @param context - Optional context to pass through (e.g., 'popover' to skip popover processing)
 * @returns An array of Tiptap JSON nodes (paragraph, heading, list, etc.)
 */
export const convertMarkdownBlockToTiptap = ((block, context?: ConverterContext): JSONContent[] => {
  const manager = getMarkdownManager();
  const doc = manager.parse(escapeBackslashesInMath(escapeTildesInMath(block.markdown)));
  const content = doc.content ?? [];

  // Post-process: popovers first (to capture complete markers), then math (to convert $...$ within text)

  const textAlign = block.custom_style?.['text-align'];

  return content.map((node, index) => {
    const processed = context?.popover ? node : processNodeForPopovers(node, block.popovers, context);
    let result = processNodeForTextColors(processNodeForCustomIcons(processNodeForMath(processed)));

    // Restore textAlign from the block's custom_style (markdown has no alignment syntax)
    if (textAlign) {
      result = { ...result, attrs: { ...result.attrs, textAlign } };
    }

    // Preserve block ID on the first node so the tiptap-to-blocks round-trip can recover it
    if (index === 0) {
      result.attrs = { ...result.attrs, id: block.id };
      // TableOfContents overwrites `id` when `data-toc-id` is null, so sync both
      if (result.type === 'heading') {
        result.attrs['data-toc-id'] = block.id;
      }
    }

    return result;
  });
}) satisfies BlockToTiptapConverter<MarkdownBlock>;

/**
 * Converts Tiptap JSON nodes back to a MarkdownBlock.
 * Uses @tiptap/markdown MarkdownManager to serialize nodes to markdown.
 * Pre-processes math nodes back to LaTeX syntax before serialization.
 * Pre-processes popover marks back to {{popover:id}}text{{/popover}} syntax.
 *
 * @param nodes - Array of Tiptap JSON nodes to convert
 * @param id - The block ID to use
 * @param customCss - Optional custom CSS classes
 * @returns A MarkdownBlock containing the serialized markdown
 */
export const convertTiptapToMarkdownBlock: ConvertTiptapToMarkdownBlockFn = (
  nodes,
  id,
  customCss?,
  context?,
  customStyle?,
) => {
  const manager = getMarkdownManager();

  const popoversMap: Record<string, PopoverData> = {};

  const processedNodes = nodes
    .map(convertTextColorsToMarkers)
    .map(convertMathNodesToText)
    .map(convertEmojiNodesToText)
    .map(convertCustomIconNodesToText)
    .map((node) => convertPopoversToMarkers(node, popoversMap, context));
  const doc: JSONContent = { type: 'doc', content: processedNodes };
  const markdown = manager.serialize(doc);

  return {
    ...createMarkdownBlock(
      id,
      markdown.trim(),
      customCss,
      Object.keys(popoversMap).length > 0 ? popoversMap : undefined,
    ),
    ...(customStyle && { custom_style: customStyle }),
  };
};

/**
 * Creates a MarkdownBlock with the given properties.
 */
export function createMarkdownBlock(
  id: string,
  markdown: string,
  customCss?: string,
  popovers?: Record<string, PopoverData>,
): MarkdownBlock {
  return {
    id,
    type: ContentBlockType.Markdown,
    markdown,
    ...(customCss && { custom_css: customCss }),
    ...(popovers && { popovers }),
  };
}
